import React, { FunctionComponent, useContext, useState } from "react";
import { Button, Dialog } from "@cmsgov/ds-medicare-gov";
import { useTranslate } from "../../../helpers";
import { CoverageSelector } from "../../../components";
import { AppContext } from "../../../app/store";
import {
  CoverageSelectorSelectedValues,
  CoverageType,
} from "../../../components/CoverageSelector/types";
import routes from "../../../app/routes";
import { AppState, ThirdPartyIntegrations } from "../../../@types";
import { hasMailOrderPharmacy } from "../../../app/store/AppStore";
import { updateStates } from "../../LandingPage/routingHelpers";
import { RouteComponentProps, withRouter } from "react-router";
import {
  AnalyticsButtonStyle,
  AnalyticsButtonType,
  Ga4Event,
  Ga4ParentComponentType,
  PlanCompareAnalyticsSubsection,
  Ga4EventType,
} from "../../../app/contexts/Analytics/types";
import englishTranslations from "../../../translations/en-US.json";
import { AnalyticsActionType } from "../../../app/contexts/Analytics/types";

const parentComponentTypeMap: Record<
  PlanCompareAnalyticsSubsection,
  Ga4ParentComponentType
> = {
  [PlanCompareAnalyticsSubsection.NEXT_PLAN_YEAR]:
    Ga4ParentComponentType.PLAN_HEADER,
  [PlanCompareAnalyticsSubsection.NEXT_STEPS]: Ga4ParentComponentType.CARD,
};

const YearOverYearExploreOptionsButton: FunctionComponent<{
  className?: string;
  subsection: PlanCompareAnalyticsSubsection;
} & RouteComponentProps> = ({ className, history, subsection }) => {
  const t = useTranslate();
  const [showCoverageSelector, setShowCoverageSelector] = useState(false);
  const { state, dispatch } = useContext(AppContext);
  const {
    zipcode,
    beneficiary,
    county,
    degradedIntegrations,
    pharmacies,
    prescriptions,
    pharmacyType,
    csr,
  } = state;
  const isBEDAPDown = degradedIntegrations.includes(
    ThirdPartyIntegrations.BEDAP
  );
  const hasMailOrderPharmacies = hasMailOrderPharmacy({
    pharmacyType,
  } as AppState);
  const pharmacyCount = pharmacies.length + (hasMailOrderPharmacies ? 1 : 0);
  const drugsCount = prescriptions.length;
  const isMissingPharmsOrDrugs = pharmacyCount === 0 || drugsCount === 0;

  const getNextRoute = () => {
    if (isBEDAPDown) {
      return routes.summary.lisQuestions;
    } else {
      return isMissingPharmsOrDrugs || csr
        ? routes.summary.drugSearchPreferences
        : routes.summary.searchResults;
    }
  };

  const destinationMap: Record<CoverageType, string> = {
    [CoverageType.MAPD]: getNextRoute(),
    [CoverageType.PDP]: getNextRoute(),
    [CoverageType.PDPAndMedigap]: getNextRoute(),
    [CoverageType.MedigapPolicy]: routes.medigap.landingPage,
    [CoverageType.learnMore]: routes.coverageWizard.options,
  };

  const handleCoverageSelectorSubmit = (
    values: CoverageSelectorSelectedValues
  ) => {
    updateStates(values, dispatch);
    if (values.coverageType) {
      const destination = destinationMap[values.coverageType];

      history.push(destination);
    }
  };

  const handleButtonClick = () => {
    dispatch({
      type: AnalyticsActionType.SEND_GA4_EVENT,
      settings: {
        button_style: AnalyticsButtonStyle.PRIMARY,
        button_type: AnalyticsButtonType.BUTTON,
        event_name: Ga4Event.YOY_CTA_ENGAGED,
        event_type: Ga4EventType.UI_INTERACTION,
        link_url: "",
        parent_component_heading: subsection,
        parent_component_type: parentComponentTypeMap[subsection],
        text: englishTranslations["yoy.explore_options.explore_other_plans"],
      },
    });
    dispatch({
      type: AnalyticsActionType.SEND_TEALIUM_EVENT,
      settings: {
        event_action: `yoy cta engaged: ${subsection}`,
        event_label:
          englishTranslations["yoy.explore_options.explore_other_plans"],
      },
    });
    setShowCoverageSelector(true);
  };

  return (
    <>
      <Button
        className={`YearOverYear__nextStepExploreButton ${className}`}
        onClick={handleButtonClick}
      >
        {t("yoy.explore_options.explore_other_plans")}
      </Button>

      {showCoverageSelector && (
        <Dialog
          heading={t("summary_page.links.find_plans_now")}
          dialogClass="mct-c-find-plans-dialog e2e-find-plans-dialog"
          headerClassName="mct-c-find-plans-dialog__header"
          onExit={() => setShowCoverageSelector(false)}
        >
          <CoverageSelector
            initialFormValues={{
              zipcode: zipcode || beneficiary?.mailing_address.zipcode,
              fips:
                county?.fips ||
                beneficiary?.mailing_address.fips_state_county ||
                "",
            }}
            onApply={values => {
              setShowCoverageSelector(false);
              handleCoverageSelectorSubmit(values);
            }}
          />
        </Dialog>
      )}
    </>
  );
};

export default withRouter(YearOverYearExploreOptionsButton);
